import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import api from '../utils/api';
import { useResizableColumns, ResizeHandle, useSortableData, SortableHeader } from '../components/shared/tableUtils';

// Same typed-signature font roster used on the public signing page —
// keeps the captured signature rendering identical between the link
// and the case manager's Agreement Preview.
const TYPED_FONTS = [
  { id: 'caveat', name: 'Caveat', stack: "'Caveat', 'Brush Script MT', cursive" },
  { id: 'dancing', name: 'Dancing Script', stack: "'Dancing Script', 'Brush Script MT', cursive" },
  { id: 'satisfy', name: 'Satisfy', stack: "'Satisfy', 'Lucida Handwriting', cursive" },
  { id: 'pacifico', name: 'Pacifico', stack: "'Pacifico', 'Brush Script MT', cursive" },
  { id: 'allura', name: 'Allura', stack: "'Allura', 'Lucida Handwriting', cursive" },
];
function fontStackForId(fontId) {
  return (TYPED_FONTS.find((f) => f.id === fontId) || TYPED_FONTS[0]).stack;
}

// Render a single signature value (drawn data URL or typed name with
// the chosen font) — mirrors SigningPage's SignatureCell.
function SignatureCell({ signatureData }) {
  if (!signatureData) return null;
  const isObj = typeof signatureData === 'object' && signatureData !== null;
  if (isObj && signatureData.type === 'typed') {
    return (
      <span style={{ fontFamily: fontStackForId(signatureData.font), fontSize: 24, color: '#0f172a', lineHeight: 1 }}>
        {signatureData.value || ''}
      </span>
    );
  }
  if (isObj && signatureData.type === 'drawn') {
    return <img src={signatureData.value} alt="signature" style={{ maxHeight: 60, maxWidth: 260, display: 'block' }} />;
  }
  if (typeof signatureData === 'string') {
    if (signatureData.startsWith('data:')) {
      return <img src={signatureData} alt="signature" style={{ maxHeight: 60, maxWidth: 260, display: 'block' }} />;
    }
    return (
      <span style={{ fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive", fontSize: 24, color: '#0f172a', lineHeight: 1 }}>
        {signatureData}
      </span>
    );
  }
  return null;
}

// Signature-table styling — kept in sync with SigningPage so the
// "what the parties saw when signing" view matches the case
// manager's Agreement Preview pixel-for-pixel.
const sigCellStyle = {
  borderBottom: '1px solid #e2e8f0',
  borderRight: '1px solid #e2e8f0',
  padding: '14px 16px',
  verticalAlign: 'top',
  fontSize: 14,
  lineHeight: 1.5,
  color: '#1f2937',
};
const sigHeaderCellStyle = {
  ...sigCellStyle,
  background: '#f8fafc',
  borderBottom: '1px solid #cbd5e1',
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#475569',
  textAlign: 'left',
};
const sigTableStyle = {
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  marginTop: 32,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  overflow: 'hidden',
};

// Strip trailing empty tables / blank wrapper elements from agreement
// HTML — DOCX imports often leave a hollow signature placeholder table
// at the end which we don't want rendered in the preview.
function stripTrailingEmptyTables(html) {
  if (!html || typeof window === 'undefined') return html || '';
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;

  // For tables, "empty" means every data row is blank. We always
  // discount the first row as a possible header — DOCX placeholder
  // grids ship with a header like "Date / Print Name / Signature /
  // Role" and empty data rows underneath, and the browser folds
  // loose <tr>s into an implicit <tbody> so a thead/tbody check
  // alone isn't enough.
  const tableIsEmpty = (table) => {
    const rows = Array.from(table.querySelectorAll('tr'));
    if (rows.length === 0) return true;
    const dataRows = rows.length > 1 ? rows.slice(1) : rows;
    return dataRows.every((row) => {
      const cells = Array.from(row.querySelectorAll('td, th'));
      if (cells.length === 0) return true;
      return cells.every((cell) => {
        const text = (cell.textContent || '').trim();
        const hasMedia = cell.querySelector('img, svg, canvas, video, input');
        return !text && !hasMedia;
      });
    });
  };

  const isBlank = (node) => {
    if (!node) return false;
    if (node.nodeType === 3) return !node.textContent.trim();
    if (node.nodeType !== 1) return false;
    const tag = node.tagName.toLowerCase();
    if (tag === 'br') return true;
    if (tag === 'table') return tableIsEmpty(node);
    if (['p', 'div', 'span'].includes(tag)) {
      const hasText = (node.textContent || '').trim().length > 0;
      const hasMedia = node.querySelector('img, svg, canvas, video, input, hr');
      return !hasText && !hasMedia;
    }
    return false;
  };

  while (wrapper.lastChild && isBlank(wrapper.lastChild)) {
    wrapper.removeChild(wrapper.lastChild);
  }
  return wrapper.innerHTML;
}

function StatusBadge({ status }) {
  const map = {
    completed: 'bg-green-50 text-green-700 border-green-200',
    signed: 'bg-green-50 text-green-700 border-green-200',
    pending: 'bg-gray-100 text-gray-600 border-gray-200',
    expired: 'bg-red-50 text-red-700 border-red-200',
  };
  const labels = {
    completed: 'Completed',
    signed: 'Signed',
    pending: 'Pending',
    expired: 'Expired',
  };
  const style = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${style}`}>
      {labels[status] || status}
    </span>
  );
}

function Toast({ kind, message, onDismiss }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [message, onDismiss]);
  if (!message) return null;
  const styles = kind === 'error'
    ? 'bg-red-50 border-red-200 text-red-700'
    : 'bg-green-50 border-green-200 text-green-700';
  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-5 right-5 z-[60] max-w-sm border rounded-lg shadow-md px-4 py-3 text-[13px] ${styles}`}
      style={{ fontFamily: 'Inter' }}
    >
      <div className="flex items-start gap-3">
        <span className="flex-1">{message}</span>
        <button onClick={onDismiss} aria-label="Dismiss" className="opacity-60 hover:opacity-100">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function SignatureDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendingClientId, setResendingClientId] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [toast, setToast] = useState({ kind: null, message: null });
  const showToast = (kind, message) => setToast({ kind, message });
  const dismissToast = () => setToast({ kind: null, message: null });

  // Sanitize the agreement HTML before injecting via dangerouslySetInnerHTML.
  // The template can contain CM-authored markup that's stored as-is in
  // `signing_requests.html_content`; if a template were ever poisoned, an
  // unsanitized render would execute scripts in the CM's session. We strip
  // tables here too, the same as before, but on the sanitized output.
  const cleanedHtml = useMemo(() => {
    const stripped = stripTrailingEmptyTables(request?.html_content || '');
    return DOMPurify.sanitize(stripped, { FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'] });
  }, [request?.html_content]);

  const fetchRequest = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api.get(`/signing/requests/${id}`)
      .then(res => setRequest(res.data?.data || res.data))
      .catch(err => {
        console.error(err);
        setLoadError(err.response?.status === 404
          ? 'Signing request not found.'
          : 'Could not load this signing request.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchRequest(); }, [fetchRequest]);

  // Shared-session shape: signers[0] holds the one signing session.
  const session = request?.signers?.[0] || {};
  const sessionUrl = session.signing_url || '';
  const caseClients = Array.isArray(session.case_clients) ? session.case_clients : [];
  const signatures = Array.isArray(session.signatures) ? session.signatures : [];

  // Derive ONE ROW PER EMAIL — the case_client's primary email plus each entry
  // in its alternate_emails[], mirroring the scale-portal Signatures widget so
  // a party that lists alternate emails gets its own signed/pending + Resend
  // row per address. Name shown is what the signer typed at signing, falling
  // back to the case_client's name.
  const inviteeRows = caseClients.flatMap((c) => {
    const alternates = Array.isArray(c.alternate_emails)
      ? c.alternate_emails.filter(Boolean)
      : [];
    const entries = [
      ...(c.email ? [{ email: c.email, isPrimary: true }] : []),
      ...alternates.map((alt) => ({ email: alt, isPrimary: false })),
    ];
    return entries.map((entry) => {
      const sig = signatures.find(
        (s) =>
          s?.email &&
          String(s.email).toLowerCase() === String(entry.email).toLowerCase(),
      );
      const typedName = typeof sig?.name === 'string' ? sig.name.trim() : '';
      const typedRole = typeof sig?.role === 'string' ? sig.role.trim() : '';
      return {
        _rowKey: `${c.client_id || c.email}-${entry.email}`,
        client_id: c.client_id,
        client_uuid: c.client_uuid,
        name: typedName || c.name || '',
        email: entry.email,
        isPrimary: entry.isPrimary,
        role: typedRole || c.role,
        status: sig ? 'signed' : 'pending',
        signedAt: sig?.signedAt || null,
        signedEmail: sig?.email || null,
        isOtherSigner: false,
      };
    });
  });

  // Append "other signers" — anyone who actually signed but is NOT one of
  // the case_clients' primary/alternate emails. Captures the full
  // signature roster on the parties table so the legally-required
  // every-signature-visible rule holds (someone who joined via shared
  // link after the SR snapshot, etc.). Other signers render with role
  // "Other Signer", status Signed, no Resend.
  const inviteeEmailSet = new Set(
    inviteeRows
      .map((r) => String(r.email || '').toLowerCase())
      .filter(Boolean),
  );
  const otherSignerRows = (signatures || [])
    .filter(
      (s) =>
        s?.email &&
        !inviteeEmailSet.has(String(s.email).toLowerCase()),
    )
    .map((s, idx) => {
      const typedName = typeof s?.name === 'string' ? s.name.trim() : '';
      const typedRole = typeof s?.role === 'string' ? s.role.trim() : '';
      return {
        _rowKey: `other-${s.email}-${s.signedAt || idx}`,
        client_id: null,
        client_uuid: null,
        name: typedName || s.email,
        email: s.email,
        isPrimary: false,
        role: typedRole,
        status: 'signed',
        signedAt: s.signedAt || null,
        signedEmail: s.email,
        isOtherSigner: true,
      };
    });

  const partyRows = [...inviteeRows, ...otherSignerRows];

  const { widths: partyColWidths, setWidth: setPartyColWidth } = useResizableColumns(
    'signatureDetail.parties',
    { name: 200, email: 240, role: 120, status: 130, signedAt: 180 }
  );
  const { sortedRows: sortedPartyRows, sort: partySort, requestSort: requestPartySort } = useSortableData(
    partyRows,
    {
      name: (r) => r.name || '',
      email: (r) => r.email || '',
      role: (r) => r.role || '',
      status: (r) => r.status || '',
      signedAt: (r) => r.signedAt || '',
    }
  );

  const { widths: sigColWidths, setWidth: setSigColWidth } = useResizableColumns(
    'signatureDetail.signatures',
    { name: 180, email: 240, role: 120, signature: 220, signedAt: 200 }
  );
  const { sortedRows: sortedSignatures, sort: sigSort, requestSort: requestSigSort } = useSortableData(
    signatures,
    {
      name: (r) => r.name || '',
      email: (r) => r.email || '',
      role: (r) => r.role || '',
      signedAt: (r) => r.signedAt || '',
      ip: (r) => r.ip || '',
    }
  );

  // Resend to all parties on the case (one scale-be call per party,
  // each fanned out CC-vs-fan-out depending on mediator type).
  const handleResend = async () => {
    setResending(true);
    try {
      const res = await api.post(`/signing/requests/${id}/resend`);
      const status = res.data?.emailDispatchStatus;
      if (status === 'failed') {
        showToast('error', 'All resends failed. Check the BE logs.');
      } else if (status === 'partial') {
        showToast('error', 'Some resends failed. Check the BE logs.');
      } else {
        showToast('success', 'Signing link resent to all parties.');
      }
      fetchRequest();
    } catch (err) {
      console.error(err);
      showToast('error', err.response?.data?.message || 'Failed to resend signing link.');
    } finally {
      setResending(false);
    }
  };

  // Per-row resend triggered from a row's Resend button. The row may
  // represent the party's PRIMARY email (in which case we hand cm-be
  // a `clientId` and it sends to the primary + CCs the alternates as
  // today) OR a single ALTERNATE email (we hand cm-be just an `email`
  // and it sends to that one address only, no CC fan-out). Adam wants
  // independent per-email resends so a CM can re-nudge only the
  // person who hasn't signed yet.
  const handleResendOne = async (row) => {
    if (!row) return;
    // Resending tracker keyed by the row's email — every row has one,
    // alternates included, so we can disable just the row in flight.
    const key = row.email || `client-${row.client_id}`;
    setResendingClientId(key);
    try {
      const payload = row.isPrimary
        ? { clientId: row.client_id }
        : { email: row.email };
      const res = await api.post(`/signing/requests/${id}/resend`, payload);
      const status = res.data?.emailDispatchStatus;
      const label = row.isPrimary ? (row.name || 'party') : (row.email || 'recipient');
      if (status === 'failed') {
        showToast('error', `Failed to resend to ${label}.`);
      } else {
        showToast('success', `Signing link resent to ${label}.`);
      }
      fetchRequest();
    } catch (err) {
      console.error(err);
      showToast('error', err.response?.data?.message || 'Failed to resend signing link.');
    } finally {
      setResendingClientId(null);
    }
  };

  const handleCopyLink = () => {
    if (!sessionUrl) {
      showToast('error', 'No signing URL on this request yet.');
      return;
    }
    if (!navigator.clipboard?.writeText) {
      showToast('error', 'Clipboard not available in this browser.');
      return;
    }
    navigator.clipboard.writeText(sessionUrl)
      .then(() => {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      })
      .catch(() => showToast('error', 'Could not copy link to clipboard.'));
  };

  // Download a zip rendered server-side from the current signing_requests
  // row. The zip contains:
  //   • <case>-agreement.pdf — clean agreement (no signatures)
  //   • <case>-signed.pdf    — signed agreement + Certificate of Completion
  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await api.get(`/signing/requests/${id}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const fname = (() => {
        const cd = res.headers?.['content-disposition'] || '';
        const m = cd.match(/filename="?([^";]+)"?/i);
        if (m && m[1]) return m[1];
        const base = (request?.case_name || request?.caseName || 'agreement')
          .replace(/[^a-z0-9-_ ]/gi, '').trim().replace(/\s+/g, '-') || 'agreement';
        return `${base}-signing-bundle.zip`;
      })();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error(err);
      // err.response.data is a Blob when the request was streaming —
      // try to read it as JSON to surface the BE's message.
      let message = 'Could not download the signing bundle.';
      try {
        if (err.response?.data && typeof err.response.data.text === 'function') {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        }
      } catch (_e) { /* keep default */ }
      showToast('error', message);
    } finally {
      setDownloading(false);
    }
  };

  const handleStatusChange = async (nextStatus) => {
    setUpdatingStatus(true);
    try {
      await api.patch(`/signing/requests/${id}/status`, { status: nextStatus });
      showToast('success', `Marked as ${nextStatus.replace('_', ' ')}.`);
      fetchRequest();
    } catch (err) {
      console.error(err);
      showToast('error', err.response?.data?.message || 'Failed to update status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col bg-white min-h-screen">
        <div className="flex items-center justify-center py-32">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex flex-col bg-white min-h-screen">
        <div className="flex flex-col items-center justify-center py-32" role="alert">
          <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <span className="text-sm text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>{loadError || 'Signing request not found.'}</span>
          <div className="flex items-center gap-3 mt-4">
            {!loadError?.includes('not found') && (
              <button
                onClick={fetchRequest}
                className="text-sm text-[#115e59] hover:underline"
                style={{ fontFamily: 'Inter' }}
              >
                Try again
              </button>
            )}
            <button
              onClick={() => navigate('/signatures')}
              className="text-sm text-[#115e59] hover:underline"
              style={{ fontFamily: 'Inter' }}
            >
              Back to Signatures
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white min-h-screen">
      {/* Header — wraps on mobile so the title row + action buttons can
          stack into two rows. Buttons stay together as a group. */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border-b border-[#e5e5e5] px-4 sm:px-6 py-3">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <button
            onClick={() => navigate('/signatures')}
            className="flex items-center gap-1.5 text-[13px] text-[#5b5a64] hover:text-[#0e1e20] transition-colors whitespace-nowrap"
            style={{ fontFamily: 'Inter' }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M8.75 10.5L5.25 7L8.75 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to Signatures
          </button>
          <span className="text-gray-300 hidden sm:inline">|</span>
          <span className="text-sm font-semibold text-[#0e1e20] truncate max-w-[260px] sm:max-w-[420px]" style={{ fontFamily: 'Inter Tight' }}>
            {request.caseName || request.case_name || 'Agreement Details'}
          </span>
          <StatusBadge status={request.status} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleCopyLink}
            disabled={!sessionUrl}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#e5e5e5] text-[12px] font-medium text-[#0e1e20] bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            style={{ fontFamily: 'Inter' }}
          >
            {copiedLink ? 'Copied!' : 'Copy Signing Link'}
          </button>
          <button
            onClick={handleResend}
            disabled={resending}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#e5e5e5] text-[12px] font-medium text-[#0e1e20] bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            style={{ fontFamily: 'Inter' }}
          >
            {resending ? 'Sending…' : 'Resend to All'}
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            title="Download a zip containing the agreement PDF and the signed agreement + Certificate of Completion PDF"
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#115e59] text-[12px] font-medium text-[#115e59] bg-white hover:bg-[#f0f9f7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            style={{ fontFamily: 'Inter' }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M8 2v8m0 0l-3-3m3 3l3-3M3 13h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {downloading ? 'Preparing zip…' : 'Download (.zip)'}
          </button>
          {request.status !== 'completed' ? (
            <button
              onClick={() => handleStatusChange('completed')}
              disabled={updatingStatus}
              className="h-8 px-4 rounded-lg bg-[#115e59] text-white text-[12px] font-medium hover:bg-[#0d4f4a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              style={{ fontFamily: 'Inter' }}
            >
              {updatingStatus ? '…' : 'Mark Complete'}
            </button>
          ) : (
            <button
              onClick={() => handleStatusChange('pending')}
              disabled={updatingStatus}
              className="h-8 px-4 rounded-lg border border-[#e5e5e5] text-[12px] font-medium text-[#5b5a64] bg-white hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              style={{ fontFamily: 'Inter' }}
            >
              {updatingStatus ? '…' : 'Reopen'}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 w-full">
        {/* Summary strip — 1 col on mobile, 2 on tablet, 4 on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <SummaryCard label="Mediator" value={request.mediator_name || '—'} />
          <SummaryCard
            label="Parties"
            value={caseClients.length ? String(caseClients.length) : '—'}
            sub={caseClients.length ? caseClients.map((c) => c.participants?.[0]?.name || '').filter(Boolean).join(', ') : undefined}
          />
          <SummaryCard
            label="Signed"
            value={`${partyRows.filter((p) => p.status === 'signed').length} / ${partyRows.length}`}
            sub={partyRows.length === 0 ? '—' : (partyRows.every((p) => p.status === 'signed') ? 'All addresses have signed' : 'Awaiting remaining addresses')}
          />
          <SummaryCard
            label="Signatures Captured"
            value={String(signatures.length)}
            sub={signatures.length ? `Last signed ${shortDate(signatures[signatures.length - 1]?.signedAt)}` : 'Nobody has signed yet'}
          />
        </div>

        {/* Parties */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
              Parties on this Case
            </h2>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr className="border-b border-[#f0f0f0]">
                <th className="text-left px-5 py-3 relative" style={{ width: partyColWidths.name }}>
                  <SortableHeader sortKey="name" sort={partySort} onSort={requestPartySort} className="text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Name</SortableHeader>
                  <ResizeHandle onResize={setPartyColWidth('name')} getCurrentWidth={() => partyColWidths.name} />
                </th>
                <th className="text-left px-5 py-3 relative" style={{ width: partyColWidths.email }}>
                  <SortableHeader sortKey="email" sort={partySort} onSort={requestPartySort} className="text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Email</SortableHeader>
                  <ResizeHandle onResize={setPartyColWidth('email')} getCurrentWidth={() => partyColWidths.email} />
                </th>
                <th className="text-left px-5 py-3 relative" style={{ width: partyColWidths.role }}>
                  <SortableHeader sortKey="role" sort={partySort} onSort={requestPartySort} className="text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Role</SortableHeader>
                  <ResizeHandle onResize={setPartyColWidth('role')} getCurrentWidth={() => partyColWidths.role} />
                </th>
                <th className="text-left px-5 py-3 relative" style={{ width: partyColWidths.status }}>
                  <SortableHeader sortKey="status" sort={partySort} onSort={requestPartySort} className="text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Status</SortableHeader>
                  <ResizeHandle onResize={setPartyColWidth('status')} getCurrentWidth={() => partyColWidths.status} />
                </th>
                <th className="text-left px-5 py-3 relative" style={{ width: partyColWidths.signedAt }}>
                  <SortableHeader sortKey="signedAt" sort={partySort} onSort={requestPartySort} className="text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Signed At</SortableHeader>
                  <ResizeHandle onResize={setPartyColWidth('signedAt')} getCurrentWidth={() => partyColWidths.signedAt} />
                </th>
                <th className="text-right px-5 py-3 text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {partyRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-sm text-gray-400" style={{ fontFamily: 'Inter' }}>
                    No parties on this signing request.
                  </td>
                </tr>
              )}
              {sortedPartyRows.map((p) => (
                <tr key={p._rowKey} className="border-b border-[#f0f0f0] hover:bg-gray-50">
                  <td className="px-5 py-3 text-[13px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter' }}>
                    <span>{p.name || '—'}</span>
                  </td>
                  <td className="px-5 py-3 text-[13px] text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>
                    <span style={{ wordBreak: 'break-all' }}>{p.email || '—'}</span>
                  </td>
                  <td className="px-5 py-3 text-[13px] text-[#5b5a64] capitalize" style={{ fontFamily: 'Inter' }}>
                    {p.role || (p.isOtherSigner ? 'Other Signer' : '—')}
                  </td>
                  <td className="px-5 py-3">
                    <StatusPill status={p.status} />
                  </td>
                  <td className="px-5 py-3 text-[13px] text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>
                    {shortDate(p.signedAt) || '—'}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {/* Per-row Resend — every signer row (primary + each
                        alternate) gets its own button. Primary rows send
                        to the party's primary email + CC the alternates
                        (existing fan-out). Alt rows send to just that
                        one email, no CC, so a CM can re-nudge a single
                        person who hasn't signed yet. Disabled when this
                        row has already signed, or once the whole request
                        is closed (status === 'completed').
                        Other-signer rows (signed by an email not on the
                        invite list) render a dash — they've already
                        signed and we have no clientId to resend to. */}
                    {p.isOtherSigner ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : (() => {
                      const rowKey = p.email || `client-${p.client_id}`;
                      const inFlight = resendingClientId === rowKey;
                      const disabled =
                        inFlight ||
                        p.status === 'signed' ||
                        request.status === 'completed' ||
                        (p.isPrimary && !p.client_id);
                      const title = p.status === 'signed'
                        ? 'This email has already signed'
                        : request.status === 'completed'
                          ? 'Signing request is closed'
                          : 'Resend signing link to this email';
                      return (
                        <button
                          type="button"
                          onClick={() => handleResendOne(p)}
                          disabled={disabled}
                          title={title}
                          className="px-2.5 py-1 text-[11px] font-medium text-[#5b5a64] bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ fontFamily: 'Inter' }}
                        >
                          {inFlight ? 'Sending…' : 'Resend'}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>{/* /overflow-x-auto */}
        </div>

        {/* Signatures Captured */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
              Signatures Captured
            </h2>
            <span className="text-[11px] text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>
              {signatures.length} on file
            </span>
          </div>
          {signatures.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-gray-400" style={{ fontFamily: 'Inter' }}>
              No signatures yet. Once parties sign, their captured signatures will appear here.
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr className="border-b border-[#f0f0f0]">
                  <th className="text-left px-5 py-3 relative" style={{ width: sigColWidths.name }}>
                    <SortableHeader sortKey="name" sort={sigSort} onSort={requestSigSort} className="text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Name</SortableHeader>
                    <ResizeHandle onResize={setSigColWidth('name')} getCurrentWidth={() => sigColWidths.name} />
                  </th>
                  <th className="text-left px-5 py-3 relative" style={{ width: sigColWidths.email }}>
                    <SortableHeader sortKey="email" sort={sigSort} onSort={requestSigSort} className="text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Email</SortableHeader>
                    <ResizeHandle onResize={setSigColWidth('email')} getCurrentWidth={() => sigColWidths.email} />
                  </th>
                  <th className="text-left px-5 py-3 relative" style={{ width: sigColWidths.role }}>
                    <SortableHeader sortKey="role" sort={sigSort} onSort={requestSigSort} className="text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Role</SortableHeader>
                    <ResizeHandle onResize={setSigColWidth('role')} getCurrentWidth={() => sigColWidths.role} />
                  </th>
                  <th className="text-left px-5 py-3 relative" style={{ width: sigColWidths.signature }}>
                    <span className="text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Signature</span>
                    <ResizeHandle onResize={setSigColWidth('signature')} getCurrentWidth={() => sigColWidths.signature} />
                  </th>
                  <th className="text-left px-5 py-3 relative" style={{ width: sigColWidths.signedAt }}>
                    <SortableHeader sortKey="signedAt" sort={sigSort} onSort={requestSigSort} className="text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Signed At</SortableHeader>
                    <ResizeHandle onResize={setSigColWidth('signedAt')} getCurrentWidth={() => sigColWidths.signedAt} />
                  </th>
                  <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>
                    <SortableHeader sortKey="ip" sort={sigSort} onSort={requestSigSort} className="text-[11px] uppercase tracking-wide font-semibold text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>IP</SortableHeader>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedSignatures.map((s, idx) => (
                  <tr key={`${s.signedAt || idx}`} className="border-b border-[#f0f0f0]">
                    <td className="px-5 py-3 text-[13px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter' }}>{s.name || '—'}</td>
                    <td className="px-5 py-3 text-[13px] text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>{s.email || '—'}</td>
                    <td className="px-5 py-3 text-[13px] text-[#5b5a64] capitalize" style={{ fontFamily: 'Inter' }}>{s.role || '—'}</td>
                    <td className="px-5 py-3"><CapturedSignature signatureData={s.signatureData} /></td>
                    <td className="px-5 py-3 text-[13px] text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>
                      {s.signedAt
                        ? new Date(s.signedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                        : '—'}
                    </td>
                    <td className="px-5 py-3 text-[12px] text-[#5b5a64]" style={{ fontFamily: 'monospace' }}>{s.ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Agreement Preview */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
              Agreement Preview
            </h2>
          </div>
          <div className="p-6 bg-[#f3f4f6]">
            {/* Lists are BARE by default; markers are opt-in per document.
                The BE stamps data-list-markers="on" when it renders a NEW
                signing request, and only when the item text doesn't already
                carry its own "4)" numbering. So an agreement sent before
                markers shipped keeps rendering exactly as it was sent, and
                Word imports that number themselves never double-number.
                Raw CSS rather than Tailwind utilities because an arbitrary
                variant can't target an attribute on the child itself. */}
            <style>{`
              .agreement-doc ul,
              .agreement-doc ol {
                padding-left: 0 !important;
                list-style: none !important;
              }
              .agreement-doc ul[data-list-markers='on'],
              .agreement-doc ol[data-list-markers='on'] {
                padding-left: 1.5rem !important;
              }
              .agreement-doc ul[data-list-markers='on'] { list-style: disc !important; }
              .agreement-doc ol[data-list-markers='on'] { list-style: decimal !important; }
              .agreement-doc ul[data-list-markers='on'] ul { list-style: circle !important; }
              .agreement-doc ol[data-list-markers='on'] ol { list-style: lower-alpha !important; }
            `}</style>
            {request.html_content ? (
              <div
                className={
                  // Mirrors the cm-fe TipTap editor typography exactly
                  // (TemplateManager.jsx → editorProps.attributes
                  // .class) so the signature-detail preview is the
                  // same canvas the CM authored against. Padding,
                  // sizes, and list rules all match the editor.
                  'agreement-doc bg-white rounded-sm shadow-sm ring-1 ring-gray-200 ' +
                  'mx-auto max-w-[820px] px-16 py-14 text-[#1f2937] leading-relaxed ' +
                  '[&_h1]:text-[28px] [&_h1]:font-bold [&_h1]:text-center [&_h1]:mt-0 [&_h1]:mb-8 ' +
                  '[&_h2]:text-[18px] [&_h2]:font-semibold [&_h2]:mt-7 [&_h2]:mb-3 ' +
                  '[&_h3]:text-[16px] [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-2 ' +
                  '[&_p]:my-3 [&_p]:text-[15px] [&_p]:leading-7 ' +
                  // Spacing only — marker + indent are decided by the
                  // data-list-markers rules in the <style> block above,
                  // which are per-document rather than blanket.
                  '[&_ul]:my-3 [&_ul]:pl-0 ' +
                  '[&_ol]:my-3 [&_ol]:pl-0 ' +
                  '[&_li]:my-1 [&_li]:text-[15px] [&_li]:leading-7 ' +
                  '[&_li>p]:my-0 ' +
                  '[&_a]:text-teal-700 [&_a]:underline ' +
                  '[&_strong]:font-semibold ' +
                  '[&_table]:w-full [&_table]:my-4 [&_table]:border-collapse ' +
                  '[&_th]:bg-gray-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-[13px] [&_th]:text-left [&_th]:font-semibold [&_th]:border [&_th]:border-gray-300 ' +
                  '[&_td]:px-3 [&_td]:py-2 [&_td]:text-[13px] [&_td]:align-top [&_td]:border [&_td]:border-gray-300'
                }
              >
                <div dangerouslySetInnerHTML={{ __html: cleanedHtml }} />
                {/* Captured signatures rendered inside the agreement
                    document, matching the layout the signers see on
                    the public signing link. The trailing empty
                    placeholder table is already stripped above, so
                    this is the single source of truth for the
                    signature section. */}
                {signatures.length > 0 && (
                  <table style={sigTableStyle}>
                    <colgroup>
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '24%' }} />
                      <col style={{ width: '36%' }} />
                      <col style={{ width: '22%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={sigHeaderCellStyle}>Date</th>
                        <th style={sigHeaderCellStyle}>Print Name</th>
                        <th style={sigHeaderCellStyle}>Signature</th>
                        <th style={{ ...sigHeaderCellStyle, borderRight: 'none' }}>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signatures.map((sig, idx) => {
                        const dateLabel = sig.date
                          || (sig.signedAt
                            ? new Date(sig.signedAt).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'long', day: 'numeric',
                              })
                            : '');
                        return (
                          <tr
                            key={`sig-${idx}-${sig.signedAt || idx}`}
                            style={{ background: idx % 2 === 0 ? '#ffffff' : '#fafbfc' }}
                          >
                            <td style={sigCellStyle}>{dateLabel}</td>
                            <td style={{ ...sigCellStyle, fontWeight: 500 }}>{sig.name || ''}</td>
                            <td style={sigCellStyle}>
                              <SignatureCell signatureData={sig.signatureData} />
                            </td>
                            <td style={{ ...sigCellStyle, borderRight: 'none', color: '#475569' }}>{sig.role || ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12 text-sm text-gray-400" style={{ fontFamily: 'Inter' }}>
                No agreement content available.
              </div>
            )}
          </div>
        </div>
      </div>
      <Toast kind={toast.kind} message={toast.message} onDismiss={dismissToast} />
    </div>
  );
}

function SummaryCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
      <div className="text-[10.5px] uppercase tracking-wide font-semibold text-[#94a3b8]" style={{ fontFamily: 'Inter' }}>
        {label}
      </div>
      <div className="mt-1 text-[18px] font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
        {value}
      </div>
      {sub && (
        <div className="mt-1 text-[11.5px] text-[#5b5a64] truncate" style={{ fontFamily: 'Inter' }} title={sub}>
          {sub}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    signed:   { bg: '#d1fae5', fg: '#065f46', label: 'Signed' },
    pending:  { bg: '#f1f5f9', fg: '#475569', label: 'Pending' },
  };
  const cfg = map[status] || map.pending;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{ backgroundColor: cfg.bg, color: cfg.fg, fontFamily: 'Inter' }}
    >
      {cfg.label}
    </span>
  );
}

function CapturedSignature({ signatureData }) {
  if (!signatureData) return <span className="text-[12px] text-gray-400">—</span>;
  if (typeof signatureData === 'object' && signatureData.type === 'typed') {
    const fonts = {
      caveat: "'Caveat', cursive",
      dancing_script: "'Dancing Script', cursive",
      satisfy: "'Satisfy', cursive",
      pacifico: "'Pacifico', cursive",
      allura: "'Allura', cursive",
    };
    return (
      <span style={{ fontFamily: fonts[signatureData.font] || 'cursive', fontSize: 22, color: '#0f172a' }}>
        {signatureData.value || ''}
      </span>
    );
  }
  if (typeof signatureData === 'object' && signatureData.type === 'drawn') {
    return <img src={signatureData.value} alt="signature" style={{ maxHeight: 48, maxWidth: 220, display: 'block' }} />;
  }
  if (typeof signatureData === 'string' && signatureData.startsWith('data:')) {
    return <img src={signatureData} alt="signature" style={{ maxHeight: 48, maxWidth: 220, display: 'block' }} />;
  }
  return <span className="text-[12px] text-gray-400">—</span>;
}

function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
